import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Link, Stack, TextField, Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import {
  extractIntakeDocumentApi,
  ingestIntakeAttachmentApi,
  queryAgencyPolicyApi,
  reviewSitePlanApi,
} from '../../api/applicationType.api';
import { friendlyCityLabel, friendlyToolLabel } from '../../utils/citizenFriendlyLabels';

const ACCENT = '#1565c0';

async function fileToAttachment(file, documentId) {
  const attachment = {
    documentId: documentId || `doc_${Date.now().toString(36)}`,
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    source: 'upload',
    uploadedAt: new Date().toISOString(),
  };
  // ~8MB binary ≈ under server MAX_ATTACHMENT_BASE64 (~1.5MB was too small for plans).
  const maxBytes = 8 * 1024 * 1024;
  if (file.size <= maxBytes) {
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    attachment.contentBase64 = btoa(binary);
  } else {
    attachment.contentTruncated = true;
  }
  return attachment;
}

/**
 * CLAW document understanding UI — package-agnostic (uses session + packageId).
 */
export default function CitizenClawDocumentsPanel({
  sessionId,
  packageId,
  session,
  onSession,
  compact = false,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [policyQ, setPolicyQ] = useState('');
  const [policyAnswer, setPolicyAnswer] = useState(null);

  const proposals = session?.meta?.pendingDocumentProposals || [];
  const review = session?.meta?.lastSitePlanReview || null;
  const lastExtract = session?.meta?.lastDocumentExtract || null;

  const clawTrail = useMemo(() => {
    const tools = [];
    if (lastExtract?.claw?.tool) tools.push(lastExtract.claw.tool);
    if (review?.claw?.tool) tools.push(review.claw.tool);
    if (policyAnswer?.claw?.tool) tools.push(policyAnswer.claw.tool);
    return tools;
  }, [lastExtract, review, policyAnswer]);

  if (!sessionId) return null;

  const runIngest = async (file, documentId) => {
    setBusy(true);
    setError(null);
    try {
      const attachment = await fileToAttachment(file, documentId);
      const data = await ingestIntakeAttachmentApi(sessionId, {
        attachment,
        extract: true,
        apply: false,
      });
      onSession?.(data?.session || data?.extract?.session || null);
      if (data?.extract?.proposals?.length) {
        /* proposals live on session.meta */
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Could not read that upload');
    } finally {
      setBusy(false);
    }
  };

  const applyProposals = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await extractIntakeDocumentApi(sessionId, {
        documentId: lastExtract?.documentId,
        apply: true,
      });
      onSession?.(data?.session || null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Could not apply extracts');
    } finally {
      setBusy(false);
    }
  };

  const runReview = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await reviewSitePlanApi(sessionId, {});
      onSession?.(data?.session || null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Site plan review failed');
    } finally {
      setBusy(false);
    }
  };

  const askPolicy = async () => {
    if (!policyQ.trim() || !packageId) return;
    setBusy(true);
    setError(null);
    try {
      const data = await queryAgencyPolicyApi({
        packageId,
        query: policyQ.trim(),
      });
      setPolicyAnswer(data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Policy query failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: compact ? 'transparent' : '#fff',
        borderRadius: compact ? 0 : 3,
        border: compact ? 'none' : '1px solid rgba(12,26,46,0.08)',
        p: compact ? 0 : 2,
      }}
      data-testid="citizen-claw-documents"
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" fontWeight={700}>
          Documents &amp; plans
        </Typography>
        {clawTrail.map((t) => (
          <Chip key={t} size="small" label={friendlyToolLabel(t)} />
        ))}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
        Upload a packet, form, or site plan. We suggest form answers for you to confirm, review drawings,
        and answer policy questions from this city&apos;s published sources.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
        <Button
          component="label"
          size="small"
          variant="outlined"
          startIcon={busy ? <CircularProgress size={14} /> : <CloudUploadOutlinedIcon />}
          disabled={busy}
          sx={{ textTransform: 'none', borderColor: ACCENT, color: ACCENT }}
        >
          Upload a document
          <input
            type="file"
            hidden
            accept=".pdf,.json,.txt,.png,.jpg,.jpeg,application/pdf,application/json,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                const kind = /plan|draw|site/i.test(f.name) ? 'site_plan' : 'document';
                runIngest(f, kind);
              }
              e.target.value = '';
            }}
          />
        </Button>
        <Button
          size="small"
          variant="text"
          disabled={busy}
          onClick={runReview}
          sx={{ textTransform: 'none' }}
          data-testid="citizen-claw-review-site-plan"
        >
          Review site plan
        </Button>
      </Stack>

      {proposals.length > 0 && (
        <Box sx={{ mb: 1.25, p: 1.25, bgcolor: 'rgba(21,101,192,0.06)', borderRadius: 2 }}>
          <Typography variant="caption" fontWeight={700}>
            Suggested answers (confirm to apply)
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.75 }}>
            {proposals.map((p) => (
              <Typography key={p.id} variant="body2">
                <strong>{p.id}</strong>
                :
                {' '}
                {String(p.value).slice(0, 120)}
                {' '}
                <Chip size="small" label={`${Math.round((p.confidence || 0) * 100)}%`} sx={{ height: 20 }} />
              </Typography>
            ))}
          </Stack>
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={applyProposals}
            sx={{ mt: 1, textTransform: 'none', bgcolor: ACCENT }}
            data-testid="citizen-claw-apply-extract"
          >
            Apply proposed fields
          </Button>
        </Box>
      )}

      {review?.findings?.length > 0 && (
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="caption" fontWeight={700}>Site plan review</Typography>
          {review.summary && (
            <Typography variant="body2" sx={{ mt: 0.5, mb: 0.5 }}>{review.summary}</Typography>
          )}
          <Stack spacing={0.35} sx={{ mt: 0.5 }}>
            {review.findings.map((f) => (
              <Alert
                key={`${f.code}-${String(f.message).slice(0, 24)}`}
                severity={f.severity === 'error' ? 'error' : f.severity === 'warning' ? 'warning' : 'info'}
                sx={{ py: 0 }}
              >
                {f.message}
              </Alert>
            ))}
          </Stack>
          {(review.policyCitations || []).length > 0 && (
            <Stack spacing={0.25} sx={{ mt: 0.75 }}>
              {review.policyCitations.slice(0, 3).map((c) => (
                <Typography key={c.id || c.url} variant="caption" color="text.secondary">
                  {c.url ? (
                    <Link href={c.url} target="_blank" rel="noopener noreferrer">{c.title}</Link>
                  ) : c.title}
                </Typography>
              ))}
            </Stack>
          )}
        </Box>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          fullWidth
          placeholder={packageId
            ? `Ask about ${friendlyCityLabel(packageId)} rules…`
            : 'Choose a city to ask about rules'}
          value={policyQ}
          onChange={(e) => setPolicyQ(e.target.value)}
          disabled={busy || !packageId}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              askPolicy();
            }
          }}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={busy || !packageId || !policyQ.trim()}
          onClick={askPolicy}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          data-testid="citizen-claw-policy-query"
        >
          Ask city rules
        </Button>
      </Stack>

      {policyAnswer?.answer && (
        <Box sx={{ mt: 1.25 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{policyAnswer.answer}</Typography>
          <Stack spacing={0.35} sx={{ mt: 0.75 }}>
            {(policyAnswer.citations || []).map((c) => (
              <Typography key={c.id || c.url} variant="caption" color="text.secondary">
                {c.url ? (
                  <Link href={c.url} target="_blank" rel="noopener noreferrer">{c.title}</Link>
                ) : c.title}
              </Typography>
            ))}
          </Stack>
          {policyAnswer.disclaimer && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {policyAnswer.disclaimer}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
